import { FunctionComponent } from "react";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { SelectField } from "@/components/SelectField/SelectField";
import { NewInRoleChangeType } from "@/enum/company-enrich-type.enum";

import { NewInRoleConfig, NewInRoleWindowPreset } from "../onboardingListTypes";
import { ConfigField, ConfigGrid, useConfigTranslation, useFieldError } from "./ConfigField";
import { ConfigFormProps } from "./configFormProps";
import { LookupMultiField, OptionMultiField } from "./FilterFields";
import { resolveIndustries } from "./useOnboardingFilterOptions";

const WINDOW_PRESETS: NewInRoleWindowPreset[] = ["30", "90", "180"];

const CHANGE_TYPES: { value: NewInRoleChangeType; labelKey: string }[] = [
	{ value: NewInRoleChangeType.NEW_ROLE, labelKey: "changeTypeNewRole" },
	{ value: NewInRoleChangeType.NEW_EMPLOYER, labelKey: "changeTypeNewEmployer" },
];

/** COMPANY_ENRICH / NEW_IN_ROLE — who changed jobs, how recently, and where. */
export const NewInRoleForm: FunctionComponent<ConfigFormProps<NewInRoleConfig>> = ({
	value,
	errors,
	options,
	onChange,
}) => {
	const ts = useConfigTranslation();
	const errorFor = useFieldError(errors);

	const toggleChangeType = (changeType: NewInRoleChangeType) => {
		const next = value.changeTypes.includes(changeType)
			? value.changeTypes.filter((entry) => entry !== changeType)
			: [...value.changeTypes, changeType];

		onChange({ ...value, changeTypes: next });
	};

	return (
		<ConfigGrid>
			<ConfigField required error={errorFor("changeTypes")} label={ts("changeTypes")}>
				<div className="flex flex-col gap-2 pt-1">
					{CHANGE_TYPES.map(({ value: changeType, labelKey }) => (
						<Checkbox
							key={changeType}
							isChecked={value.changeTypes.includes(changeType)}
							onChange={() => toggleChangeType(changeType)}
						>
							<span className="text-ssm">{ts(labelKey)}</span>
						</Checkbox>
					))}
				</div>
			</ConfigField>

			<ConfigField label={ts("startedWithin")}>
				<SelectField
					hideEmptyValue
					handleSelect={(preset) =>
						onChange({ ...value, windowPreset: String(preset) as NewInRoleWindowPreset })
					}
					options={WINDOW_PRESETS.map((preset) => ({
						title: ts(`startedWithin${preset}`),
						value: preset,
					}))}
					selectedOption={value.windowPreset}
					testId="onboarding-newInRole-window"
				/>
			</ConfigField>

			<OptionMultiField
				error={errorFor("newInRoleFilters")}
				label={ts("department")}
				options={options.departmentOptions}
				placeholder={ts("departmentPlaceholder")}
				values={value.department}
				onChange={(department) => onChange({ ...value, department })}
			/>

			<OptionMultiField
				label={ts("seniority")}
				options={options.seniorityOptions}
				placeholder={ts("seniorityPlaceholder")}
				values={value.seniority}
				onChange={(seniority) => onChange({ ...value, seniority })}
			/>

			<LookupMultiField
				fetchFn={options.fetchCountries}
				label={ts("personLocation")}
				placeholder={ts("personLocationPlaceholder")}
				testId="onboarding-newInRole-countries"
				titleOf={options.countryTitleOf}
				values={value.countries}
				onChange={(countries) => onChange({ ...value, countries })}
			/>

			<OptionMultiField
				label={ts("industries")}
				options={options.industryOptions}
				placeholder={ts("industriesPlaceholder")}
				values={value.industries.map((industry) => industry.name)}
				onChange={(names) =>
					onChange({ ...value, industries: resolveIndustries(names, options.industriesByName) })
				}
			/>

			<OptionMultiField
				label={ts("employees")}
				options={options.employeeOptions}
				placeholder={ts("employeesPlaceholder")}
				values={value.employees}
				onChange={(employees) => onChange({ ...value, employees })}
			/>
		</ConfigGrid>
	);
};
